<script setup lang="ts">
import { computed, defineEmits, ref } from 'vue'
import { type FormInst, type FormRules, NForm, NFormItem, useMessage } from 'naive-ui'
import { t } from '@/locales'
import { setAccountGoogle } from '@/api/auth'
import { validateEmail, validatePassword } from '@/utils/validators'
import { useAuthStore, useUserStore } from '@/store'

const props = defineProps<{
  show: boolean
}>()

const emit = defineEmits(['onClose'])

const message = useMessage()
const userStore = useUserStore()
const authStore = useAuthStore()

const showModal = ref(props.show)
const userInfo = computed(() => userStore.userInfo)
const modelRef = ref({
  email: userInfo.value.email || null,
  password: null,
  confirmPassword: null,
})

const formRef = ref<FormInst | null>(null)
const loadding = ref<boolean>(false)

const validateConfirmPassword = (rule: any, value: string) => {
  if (value && value !== modelRef.value.password)
    return new Error(t('auth.validation.password_mismatch'))
  return true
}

const rulesRef: FormRules = {
  email: [
    {
      required: true,
      message: t('auth.validation.required_email'),
      trigger: ['blur', 'input', 'change'],
    },
    {
      validator: validateEmail,
      trigger: ['blur', 'input', 'change'],
    },
  ],
  password: [
    {
      required: true,
      message: t('auth.validation.required_new_password'),
      trigger: ['blur', 'input', 'change'],
    },
    {
      validator: validatePassword,
      trigger: ['blur', 'input', 'change'],
    },
  ],
  confirmPassword: [
    {
      required: true,
      message: t('auth.validation.required_confirm'),
      trigger: ['blur', 'input', 'change'],
    },
    {
      validator: validateConfirmPassword,
      trigger: ['blur', 'input', 'change'],
    },
  ],
}

const closeModal = (reload: boolean, info?: any) => {
  showModal.value = false
  emit('onClose', reload)
  if (reload)
    emit('onClose', reload, info)
  else
    emit('onClose', reload)
}

const handleCancel = () => {
  closeModal(false)
}

const onSetPassword = async () => {
  if (!modelRef.value.email)
    return

  try {
    loadding.value = true
    const params = {
      email: modelRef.value.email,
      password: modelRef.value.password,
      confirmPassword: modelRef.value.confirmPassword,
      token: authStore.token,
    }
    const { data } = await setAccountGoogle(params)
    if (data?.code === 'API000')
      closeModal(true, params)

    else
      message.error('Đã có lỗi xảy ra')
  }
  catch (e: any) {
    message.error(e?.response?.data?.message || 'Đã có lỗi xảy ra')
  }
  finally {
    loadding.value = false
  }
}

const handleSetPassword = () => {
  formRef.value?.validate().then(async () => {
    await onSetPassword()
  }).catch((e: any) => {
    console.log('e', e)
  }).finally(() => {})
}
</script>

<template>
  <AppModal
    v-model:show="showModal"
    preset="card"
    transform-origin="center"
    style="width: 525px"
    class="!rounded-2xl"
    content-class="!px-4 !py-0"
    header-class="!p-4"
    footer-class="!p-4 !w-full !pt-3"
    :mask-closable="false"
    @after-leave="handleCancel"
    @esc="handleCancel"
  >
    <template #header>
      <div>
        <div class="text-center text-2xl font-semibold text-gray-900 dark:text-neutral-100 mb-4">
          {{ $t('auth.setup_password') }}
        </div>
        <div class="text-center font-normal text-sm leading-6 max-w-xl mx-auto">
          {{ $t('auth.setupPasswordDescription') }}
        </div>
      </div>
    </template>

    <div class="w-full">
      <NForm
        ref="formRef"
        :model="modelRef"
        :rules="rulesRef"
        :native-options="{ autocomplete: 'off' }"
        @keydown.enter.prevent="handleSetPassword"
      >
        <input type="text" autocomplete="off" style="display: none">
        <input type="password" autocomplete="new-password" style="display: none">
        <NFormItem :label="t('auth.login_email')" path="email">
          <AppInput
            :value="modelRef.email"
            disabled
            size="medium"
            class=""
            autocomplete="off"
            autocapitalize="off"
            spellcheck="false"
          />
        </NFormItem>
        <NFormItem :label="t('auth.password')" path="password">
          <AppInput
            v-model:value="modelRef.password"
            type="password"
            placeholder="******"
            show-password-on="mousedown"
            size="medium"
            class=""
          />
        </NFormItem>
        <NFormItem :label="t('auth.confirm_password')" path="confirmPassword">
          <AppInput
            v-model:value="modelRef.confirmPassword"
            type="password"
            placeholder="******"
            show-password-on="mousedown"
            size="medium"
            class=""
          />
        </NFormItem>
      </NForm>
    </div>

    <template #footer>
      <div class="w-full flex gap-3 justify-center items-center">
        <AppButton
          class=" max-w-[180px] !w-full"
          size="medium"
          @click="handleCancel"
        >
          {{ $t('common.cancel') }}
        </AppButton>
        <AppButton
          type="primary"
          class=" max-w-[180px] !w-full"
          size="medium"
          :loading="loadding"
          @click="handleSetPassword"
        >
          {{ $t('common.save') }}
        </AppButton>
      </div>
    </template>
  </AppModal>
</template>

<style scoped>

</style>
